import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Save, Settings, Bell, Mail, Phone, Camera } from 'lucide-react'
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { createClient } from '@/lib/supabase/client'
import { toast } from 'sonner'

export default function ConfiguracoesPage() {
  const { user, signOut } = useAuth()
  const supabase = createClient()
  const [name, setName] = useState((user as any)?.name || '')
  const [avatarUrl, setAvatarUrl] = useState((user as any)?.avatar_url || '')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string>(avatarUrl)

  function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) {
      setAvatarFile(file)
      setAvatarPreview(URL.createObjectURL(file))
    }
  }

  async function handleProfileUpdate(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    try {
      let finalAvatarUrl = avatarUrl
      if (avatarFile) {
        // Upload do avatar para o Supabase Storage (bucket 'avatars')
        const fileExt = avatarFile.name.split('.').pop()
        const fileName = `${user?.id}.${fileExt}`
        const { data, error: uploadError } = await supabase.storage.from('avatars').upload(fileName, avatarFile, { upsert: true })
        if (uploadError) throw uploadError
        const { data: publicUrlData } = supabase.storage.from('avatars').getPublicUrl(fileName)
        finalAvatarUrl = publicUrlData.publicUrl
      }
      const { error } = await supabase.from('users').update({ name, avatar_url: finalAvatarUrl }).eq('id', user?.id)
      if (error) throw error
      setAvatarUrl(finalAvatarUrl)
      toast.success('Perfil atualizado com sucesso!')
    } catch (error) {
      toast.error('Erro ao atualizar perfil.')
    } finally {
      setLoading(false)
    }
  }

  async function handlePasswordChange(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    try {
      if (password !== confirmPassword) throw new Error('As senhas não coincidem')
      const { error } = await supabase.auth.updateUser({ password })
      if (error) throw error
      toast.success('Senha alterada com sucesso!')
      setPassword('')
      setConfirmPassword('')
    } catch (error) {
      toast.error('Erro ao alterar senha.')
    } finally {
      setLoading(false)
    }
  }

  async function handleDeleteAccount() {
    if (!confirm('Tem certeza que deseja deletar sua conta? Esta ação não poderá ser desfeita.')) return
    setLoading(true)
    try {
      // Deleta o usuário do Auth
      const { error } = await supabase.rpc('delete_user', { uid: user?.id })
      if (error) throw error
      toast.success('Conta deletada com sucesso!')
      signOut()
    } catch (error) {
      toast.error('Erro ao deletar conta.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-xl mx-auto space-y-8 py-8">
      <h1 className="text-3xl font-bold mb-8 text-center">Configurações do Usuário</h1>
      <form onSubmit={handleProfileUpdate} className="space-y-6 p-6 border rounded-2xl bg-white shadow">
        <h2 className="text-xl font-semibold mb-2">Editar Perfil</h2>
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-24 h-24">
            <img
              src={avatarPreview || '/default-avatar.png'}
              alt="Avatar Preview"
              className="w-24 h-24 rounded-full object-cover border"
            />
            <label htmlFor="avatar-upload" className="absolute bottom-0 right-0 bg-primary text-white rounded-full p-2 cursor-pointer shadow-lg hover:bg-primary/80 transition-colors">
              <Camera className="w-5 h-5" />
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </label>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Nome</label>
          <Input value={name} onChange={e => setName(e.target.value)} required />
        </div>
        <Button type="submit" disabled={loading} className="w-full">Salvar Alterações</Button>
      </form>
      <form onSubmit={handlePasswordChange} className="space-y-4 p-6 border rounded-2xl bg-white shadow">
        <h2 className="text-xl font-semibold mb-2">Trocar Senha</h2>
        <div>
          <label className="block text-sm font-medium mb-1">Nova Senha</label>
          <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Confirmar Nova Senha</label>
          <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
        </div>
        <Button type="submit" disabled={loading} className="w-full">Alterar Senha</Button>
      </form>
      <div className="p-6 border rounded-2xl bg-white shadow">
        <h2 className="text-xl font-semibold text-red-600 mb-2">Deletar Conta</h2>
        <p className="mb-4 text-sm text-muted-foreground">Esta ação é irreversível. Todos os seus dados serão apagados.</p>
        <Button type="button" variant="destructive" onClick={handleDeleteAccount} disabled={loading} className="w-full">Deletar Conta</Button>
      </div>
    </div>
  )
} 